import { isLikelyRdTitle } from "./rdTitleMatch.js";

export const JOB_SOURCES_FIELD = "JobPostingSources";
export const JOB_POSTINGS_FIELD = "JobPostings";

export const SOURCE_KINDS = [
  { id: "careers", label: "Careers" },
  { id: "ats", label: "ATS" },
  { id: "linkedin", label: "LinkedIn" },
  { id: "other", label: "Other" },
];

const MAX_SOURCES = 5;
const DETAIL_SCRAPE_CAP = 10;

export function normalizeDomain(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .split("/")[0]
    .split("?")[0];
}

export function normalizeUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  try {
    const withProtocol = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    const url = new URL(withProtocol);
    url.hash = "";
    // Drop trailing slash for stable keys (except bare origin).
    if (url.pathname.length > 1 && url.pathname.endsWith("/")) {
      url.pathname = url.pathname.replace(/\/+$/, "");
    }
    return url.toString();
  } catch {
    return raw;
  }
}

export function parseJsonArray(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(String(raw));
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function suggestCareersUrls(domain) {
  const d = normalizeDomain(domain);
  if (!d) return [];
  return [
    { url: `https://${d}/careers`, label: "Careers", kind: "careers" },
    { url: `https://${d}/jobs`, label: "Jobs", kind: "careers" },
  ];
}

export function suggestCareersUrl(domain) {
  return suggestCareersUrls(domain)[0]?.url || "";
}

export function inferSourceKind(url) {
  const u = String(url || "").toLowerCase();
  if (u.includes("linkedin.com")) return "linkedin";
  if (
    u.includes("greenhouse.io") ||
    u.includes("ashbyhq.com") ||
    u.includes("lever.co") ||
    u.includes("workday") ||
    u.includes("myworkdayjobs.com") ||
    u.includes("jobs.jobvite.com") ||
    u.includes("apply.workable.com")
  ) {
    return "ats";
  }
  if (u.includes("/careers") || u.includes("/jobs") || u.includes("/join")) {
    return "careers";
  }
  return "other";
}

function makeId(prefix) {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36).slice(-4)}`;
}

export function normalizeSource(raw = {}) {
  const url = normalizeUrl(raw.url || raw.href || "");
  if (!url) return null;
  return {
    id: raw.id || makeId("src"),
    url,
    label: String(raw.label || inferSourceKind(url)).trim() || "Source",
    kind: SOURCE_KINDS.some((k) => k.id === raw.kind)
      ? raw.kind
      : inferSourceKind(url),
  };
}

export function normalizeSources(list) {
  const seen = new Set();
  const out = [];
  for (const item of list || []) {
    const source = normalizeSource(item);
    if (!source) continue;
    const key = source.url.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(source);
    if (out.length >= MAX_SOURCES) break;
  }
  return out;
}

export function openingMergeKey(opening) {
  const url = normalizeUrl(opening?.url || "");
  if (url) return `url:${url.toLowerCase()}`;
  const title = String(opening?.title || "")
    .trim()
    .toLowerCase();
  const location = String(opening?.location || "")
    .trim()
    .toLowerCase();
  return `title:${title}|${location}`;
}

export function normalizeOpening(raw = {}, { sourceUrl = "" } = {}) {
  const title = String(raw.title || raw.name || "").trim();
  if (!title) return null;
  const salaryNum = Number(raw.salary);
  const salary = Number.isFinite(salaryNum) && salaryNum > 0 ? salaryNum : null;
  const url = normalizeUrl(raw.url || raw.href || "") || "";
  return {
    id: raw.id || makeId("job"),
    title,
    location: String(raw.location || "").trim(),
    salary,
    url,
    sourceUrl: normalizeUrl(raw.sourceUrl || sourceUrl) || sourceUrl || "",
    scrapedAt: raw.scrapedAt || new Date().toISOString(),
    isRd: typeof raw.isRd === "boolean" ? raw.isRd : isLikelyRdTitle(title),
  };
}

export function normalizeOpenings(list) {
  return (list || [])
    .map((item) => normalizeOpening(item))
    .filter(Boolean);
}

/**
 * Additive merge: keep every existing opening; append only keys not already present.
 * New roles win on title/salary/location when the same key already exists? Plan says
 * never wipe — keep existing as-is and only append.
 */
export function mergeOpeningsAdditive(existing = [], incoming = []) {
  const existingNorm = normalizeOpenings(existing);
  const keys = new Set(existingNorm.map(openingMergeKey));
  const added = [];
  for (const raw of incoming || []) {
    const next = normalizeOpening(raw);
    if (!next) continue;
    const key = openingMergeKey(next);
    if (keys.has(key)) continue;
    keys.add(key);
    added.push(next);
  }
  return { openings: [...existingNorm, ...added], added };
}

export function serializeSources(sources) {
  return JSON.stringify(normalizeSources(sources));
}

export function serializeOpenings(openings) {
  return JSON.stringify(normalizeOpenings(openings));
}

export { MAX_SOURCES, DETAIL_SCRAPE_CAP };
